import { ELEMENT_LABEL, type ClassId, type Element } from './constants.ts';
import { ROMAN } from './progression.ts';
import type { Dice } from './dice.ts';

/**
 * Six loadout slots, chosen before the match. Levelling raises the tier of what
 * you already carry rather than granting new spells, so nobody opens a menu
 * mid-fight. Each slot has two or three candidates, which is where build
 * variety lives.
 *
 * Two classes also carry an innate ability that costs no slot, because a team
 * without it is broken rather than merely different: the Cleric can always
 * raise the dead, and the Mentalist can always fly.
 */
export type SlotRole =
  | 'primary' | 'secondary' | 'area' | 'control' | 'utility' | 'defense'
  /**
   * Not a loadout slot at all: what a class carries because of what it is.
   *
   * Kept in the same union so an innate is a `SpellDef` like anything else and
   * every piece of machinery that reads one — costs, cooldowns, tiers, the bar —
   * works on it without a special case.
   */
  | 'innate';

export const SLOT_ORDER: readonly SlotRole[] = [
  'primary',
  'secondary',
  'area',
  'control',
  'utility',
  'defense',
];

export type SpellKind =
  /** Pulls a fallen ally to the caster. They arrive still a ghost. */
  | 'call'
  /** Brings a ghost at the caster's feet back to life. */
  | 'resurrect'
  /** Held rather than cast. On the bar to say whether it is available. */
  | 'flight'
  | 'projectile'
  | 'ball'
  | 'storm'
  | 'touch'
  | 'wall'
  | 'sign'
  | 'heal'
  | 'heal-other'
  | 'buff-other'
  | 'bolt'
  | 'buff'
  | 'debuff'
  /**
   * Lands on the nearest enemy in range with no aiming at all — lightning that
   * simply arrives around their head. Distinct from `touch`, which still wants
   * the crosshair on somebody. The trade for never missing is low damage.
   */
  | 'arc';

export interface SpellDef {
  id: string;
  /** Base name. The tier numeral is appended at display time. */
  name: string;
  /**
   * Per-tier names, for the handful of spells that become a different spell
   * rather than a stronger one. Barrier Law is the whole reason this exists: at
   * tier V an Airwall is a Stonewall, and calling it "Airwall V" on the HUD
   * would contradict what it now does.
   */
  tierNames?: readonly string[];
  school: string;
  cls: ClassId;
  slot: SlotRole;
  kind: SpellKind;
  element: Element;
  /** One entry per tier, index 0 = tier I. */
  dice?: readonly Dice[];
  ppCost: readonly number[];
  fatigueCost: number;
  /** Seconds. The modern addition that stops single-spell spam. */
  cooldown: number;
  /** Windup before release. Zero for darts, real for the big ones. */
  castTime?: number;
  projectileSpeed?: number;
  /** How far an arc reaches, metres. */
  range?: number;
  /**
   * Ticks of burning added on hit, per tier. Fire's signature — see
   * BURN_* in constants for how the stack behaves.
   */
  burnTicks?: readonly number[];

  /**
   * How much a hit slows the target, and for how long. Ice's signature.
   *
   * The counterpart to fire's burn: fire takes health after the fact and cold
   * takes *distance*, which against a fleeing mage is often the more valuable of
   * the two. Both are riders on an ordinary hit rather than separate spells,
   * because "the ice one also slows" is a thing a player should be able to hold
   * in their head without reading anything.
   *
   * `chill` is the fraction of speed left, so 0.65 is a mage moving at
   * two-thirds pace. Not per tier: a slow that deepens with level would make a
   * high Magician's primary a soft root, which is the Cleric's job and priced
   * like it.
   */
  chill?: number;
  chillSeconds?: number;
  /**
   * Damage per tick of a lightning burn, by rank. Fixed duration, refreshed
   * rather than extended — see `PAIN_TICKS`.
   */
  shockDamage?: readonly number[];
  /** Power torn out of the target on hit, per tier. Void Law's signature. */
  ppDrain?: readonly number[];
  /**
   * How hard a hit shoves, in metres per second, by rank.
   *
   * Along the flight path rather than away from the caster, so a shot that
   * catches somebody side-on pushes them across their own line of travel — which
   * is what makes it worth aiming at a doorway rather than at a mage.
   */
  knockback?: readonly number[];

  /**
   * How far this projectile travels before giving up, if not the usual.
   *
   * A slow projectile with the standard range spends four seconds in the air and
   * arrives somewhere nobody is. Range is what keeps it a *close* weapon.
   */
  maxRange?: number;

  /**
   * How many bodies leave the hand at once. One unless stated.
   *
   * Each is a separate projectile with its own roll and its own hit, which is
   * the whole point: a volley can land two of three, and one projectile with a
   * bigger die never can.
   */
  volley?: number;

  /**
   * Extra damage per ten metres between the caster and whoever is hit.
   *
   * Nearly every weapon in every game falls off with range; this rewards it,
   * because the thing being paid for is not the shot but the *sightline*. A
   * mage who has found a long open lane and stood still in it for a third of a
   * second to charge has done something a Shock Bolt never asks for, and the
   * bonus is what makes that worth doing rather than merely possible.
   *
   * Counted in whole ten-metre steps, so the number is one a player can arrive
   * at by eye rather than a curve they have to feel out.
   */
  farBonus?: number;

  /**
   * Extra enemies a blast jumps to beyond the ones it caught, by rank.
   *
   * A chain reaches *past* the blast rather than widening it: the jump starts
   * from whoever was hit and finds somebody the explosion missed, which is what
   * makes it feel like lightning rather than a bigger bang.
   */
  chainTargets?: readonly number[];
  /**
   * Elements this spell can be tuned to, cycled with Tab while it is readied.
   * Only the protection lines have this.
   */
  elementChoices?: readonly Element[];
  /** Name with `{element}` substituted, for the spells above. */
  nameTemplate?: string;
  /** Blast radius in metres, for ball and storm kinds. */
  radius?: number;
  /** Effect lifetime in seconds, for walls, signs and buffs. */
  duration?: number;
  /**
   * Per-tier lifetime, where levelling lengthens an effect as well as
   * strengthening it. Takes precedence over `duration`.
   */
  durations?: readonly number[];
  /**
   * When this spell's tiers arrive, overriding its slot's schedule.
   *
   * The class innates are not carried in a loadout slot, so they have no slot
   * schedule to inherit. Resurrect climbs on its own ladder and Call Ghost has
   * one rung it never leaves.
   */
  unlockLevels?: readonly number[];
  /**
   * Rolled once a second for the life of the effect rather than once when it
   * lands. A bleed that rolls its whole total up front and spreads it thinly is
   * a rounding error a tick; rolling per tick is what makes it a bleed.
   */
  ticks?: boolean;
  notes?: string;
}

/** Shorthand for a tier dice ramp. */
const d = (count: number, sides: number, flat?: number): Dice => ({ count, sides, flat });

const RAMP_PRIMARY = [d(2, 4), d(2, 6), d(3, 6), d(3, 8), d(4, 8)];
/**
 * A primary that cannot miss, which has to cost something.
 *
 * Five ranks that grow like a primary's rather than a utility's, but a step
 * behind one at every rank — because an arc is aimed by standing near somebody
 * and a dart is aimed. Making it match `RAMP_PRIMARY` outright would leave no
 * reason ever to fire Shock Dart, which is the one spell in the class that
 * rewards the flick aim a flying Mentalist is in a position to use.
 */
const RAMP_PRIMARY_ARC = [d(1, 6), d(2, 4), d(2, 6), d(3, 6), d(3, 8)];
const RAMP_SECONDARY = [d(3, 6), d(4, 6), d(4, 8), d(5, 8), d(6, 8)];
const RAMP_AREA = [d(3, 8), d(4, 8), d(5, 8), d(6, 8), d(7, 8)];
const RAMP_CONTROL = [d(1, 6), d(2, 6), d(2, 8), d(3, 8), d(3, 10)];
const RAMP_HEAL = [d(2, 6), d(3, 6), d(4, 8), d(5, 8), d(8, 8)];
/** Deliberately under every other line. Never missing is the rest of the pay. */
/**
 * Ice Dart alone. It was on the shared primary ramp while also being the fastest
 * projectile in the class and carrying a slow, which made it strictly better
 * than both of its neighbours — there was no reason to ever pick Burn or Sharp
 * Edges. This line is well under the others, and the speed is the pay.
 */
const RAMP_ICE_DART = [d(2, 3), d(2, 4), d(3, 4), d(3, 6), d(4, 6)];
/**
 * Bleeding rolls this *every second*, not once. Tier I is three seconds of 1d4,
 * which is around seven damage rather than the three and a half a single 1d6
 * spread over six seconds came to — that version was a rounding error the target
 * regenerated through without noticing.
 */
const RAMP_BLEED = [d(1, 4), d(1, 6), d(2, 4), d(2, 6), d(3, 4)];
/** Bleeds get longer as well as harder. */
const BLEED_DURATIONS = [3, 4, 6, 7, 8];


const COST_PRIMARY = [1, 1, 2, 2, 3];
const COST_SECONDARY = [3, 4, 5, 6, 7];
const COST_AREA = [6, 8, 10, 12, 15];
const COST_CONTROL = [2, 3, 4, 5, 6];
const COST_UTILITY = [3, 4, 5, 6, 7];
const COST_DEFENSE = [2, 3, 4, 5, 6];

/** Burning ticks per tier. One more tick per tier, from a different floor. */
const BURN_DART = [1, 2, 3, 4, 5];
const BURN_BOLT = [2, 3, 4, 5, 6];
/**
 * Damage a Pain tick does, by rank.
 *
 * Lightning rather than fire, and the difference is not only the colour: a burn
 * *extends* when it is reapplied, which rewards a Magician for landing dart
 * after dart, and a shock *refreshes* — four seconds from the last one however
 * many have landed. Pain is a heavy, slow, deliberate spell and stacking it
 * would make the Mentalist a damage class it is not.
 */
const PAIN_TICKS = [1, 1, 2, 2, 3];

/**
 * How many *extra* enemies Electrocute jumps to, by rank.
 *
 * One from the very first rank, because chaining is the spell's whole identity
 * and a first-rank Electrocute that does not chain is a worse Fire Ball. The
 * second jump at rank II is the reward for levelling it, and it stops there —
 * a chain that grows with rank ends up hitting a whole team from one cast.
 */
const CHAIN_ELECTROCUTE = [1, 2, 2, 2, 2];

/**
 * What *one* axe of a Wounding Triad rolls, by rank.
 *
 * A third of a secondary each, near enough, since three of them land. Kept
 * modest because all three arriving is the good case rather than the expected
 * one — a Cleric who has lined a mage up properly should be rewarded, not a
 * Cleric who pointed in the right direction.
 */
const RAMP_TRIAD = [d(1, 6), d(1, 8), d(2, 4), d(2, 6), d(2, 8)];

/**
 * How hard a Stone Fist shoves, metres per second, by rank.
 *
 * Enough to break somebody off a doorway or out of a pool, not enough to throw
 * them across a room — a shove that relocates a mage takes the fight out of
 * their hands, and this is meant to interrupt rather than to decide.
 */
const KNOCK_FIST = [7, 8, 9, 10, 11];

/**
 * How hard ice bites, and for how long.
 *
 * A third off the top speed. Enough that a chilled mage loses a fight they were
 * winning by running, and not so much that the Magician has quietly acquired the
 * Cleric's control spell for the price of a primary — Cramp costs a control slot
 * and takes forty per cent for three seconds, and this must clearly be the
 * lesser thing.
 *
 * The dart's is short because it is fired constantly and a stacking-length slow
 * from a spammable primary is a root by another name. The storm's lasts as long
 * as somebody is standing in it, which is the whole point of a storm.
 */
const CHILL_DART = 0.7;
const CHILL_DART_SECONDS = 1.2;
const CHILL_BALL = 0.65;
const CHILL_BALL_SECONDS = 2.5;
const CHILL_STORM = 0.6;
const CHILL_STORM_SECONDS = 1.5;
const BURN_BALL = [4, 5, 6, 7, 8];

/** Power torn out on hit, per tier. */
const DRAIN_SPIKE = [1, 1, 2, 2, 3];
const DRAIN_BOLT = [2, 3, 4, 5, 6];
const DRAIN_BALL = [3, 4, 5, 6, 8];

/** The four schools a protection can be tuned against. */
const PROTECTABLE: readonly Element[] = ['fire', 'cold', 'earth', 'light'];

function spell(s: SpellDef): SpellDef {
  return s;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAGICIAN — Fire, Ice, Earth. Raw damage, no shrine work, pool-dependent.
// ─────────────────────────────────────────────────────────────────────────────
const MAGICIAN: SpellDef[] = [
  spell({ id: 'burn', name: 'Burn', school: 'Fire Law', cls: 'magician', slot: 'primary', kind: 'projectile', element: 'fire', dice: RAMP_PRIMARY, burnTicks: BURN_DART, ppCost: COST_PRIMARY, fatigueCost: 16, cooldown: 0.35, projectileSpeed: 50, notes: 'Sets the target burning. Every hit extends the burn.' }),
  spell({ id: 'ice_dart', name: 'Ice Dart', school: 'Ice Law', cls: 'magician', slot: 'primary', kind: 'projectile', element: 'cold', dice: RAMP_ICE_DART, ppCost: COST_PRIMARY, fatigueCost: 16, cooldown: 0.3, projectileSpeed: 58, chill: CHILL_DART, chillSeconds: CHILL_DART_SECONDS, notes: 'Fastest and flattest of the three, and it slows on hit.' }),
  spell({ id: 'sharp_edges', name: 'Sharp Edges', school: 'Earth Law', cls: 'magician', slot: 'primary', kind: 'projectile', element: 'earth', dice: RAMP_PRIMARY, ppCost: COST_PRIMARY, fatigueCost: 18, cooldown: 0.45, projectileSpeed: 42, notes: 'Slowest primary, biggest single hit.' }),

  spell({ id: 'fire_bolt', name: 'Fire Bolt', school: 'Fire Law', cls: 'magician', slot: 'secondary', kind: 'bolt', element: 'fire', dice: RAMP_SECONDARY, burnTicks: BURN_BOLT, notes: 'Burns hotter and for twice as long as Burn.', ppCost: COST_SECONDARY, fatigueCost: 26, cooldown: 1.6, castTime: 0.25, range: 44 }),
  /*
   * The one secondary that is not a beam.
   *
   * Stone Bolt was a hitscan like the other five, which left the Magician with
   * two aimed instants and no reason ever to have both. A slow, heavy, visible
   * thing that shoves people is a different spell entirely: it can be dodged, it
   * can be walked around, and it is worth firing at somebody's *feet* to stop
   * them reaching a doorway. The damage is unchanged; everything else about how
   * it is used is not.
   */
  spell({ id: 'stone_fist', name: 'Stone Fist', school: 'Earth Law', cls: 'magician', slot: 'secondary', kind: 'projectile', element: 'earth', dice: RAMP_SECONDARY, knockback: KNOCK_FIST, ppCost: COST_SECONDARY, fatigueCost: 26, cooldown: 1.6, castTime: 0.3, projectileSpeed: 22, maxRange: 26, notes: 'A large projectile that knocks the target back.' }),

  spell({ id: 'fire_ball', name: 'Fire Ball', school: 'Fire Law', cls: 'magician', slot: 'area', kind: 'ball', element: 'fire', dice: RAMP_AREA, burnTicks: BURN_BALL, ppCost: COST_AREA, fatigueCost: 40, cooldown: 5, castTime: 0.5, projectileSpeed: 26, radius: 4.5, notes: 'Sets everything in the blast burning, white hot.' }),
  spell({ id: 'shrapnel_ball', name: 'Shrapnel Ball', school: 'Earth Law', cls: 'magician', slot: 'area', kind: 'ball', element: 'earth', dice: RAMP_AREA, ppCost: COST_AREA, fatigueCost: 40, cooldown: 5, castTime: 0.5, projectileSpeed: 24, radius: 5.5, notes: 'Wider blast range, but less damage.' }),
  spell({ id: 'cold_ball', name: 'Cold Ball', school: 'Ice Law', cls: 'magician', slot: 'area', kind: 'ball', element: 'cold', dice: RAMP_AREA, ppCost: COST_AREA, fatigueCost: 40, cooldown: 5, castTime: 0.5, projectileSpeed: 26, radius: 4.5, chill: CHILL_BALL, chillSeconds: CHILL_BALL_SECONDS, notes: 'Slows everything caught in the blast.' }),

  spell({ id: 'fire_storm', name: 'Fire Storm', school: 'Fire Law', cls: 'magician', slot: 'control', kind: 'storm', element: 'fire', dice: RAMP_CONTROL, ppCost: COST_CONTROL, fatigueCost: 34, cooldown: 8, castTime: 0.8, radius: 6, duration: 4, notes: 'Ground-targeted area control. Ticks on everyone standing in it.' }),
  spell({ id: 'ice_storm', name: 'Ice Storm', school: 'Ice Law', cls: 'magician', slot: 'control', kind: 'storm', element: 'cold', dice: RAMP_CONTROL, ppCost: COST_CONTROL, fatigueCost: 34, cooldown: 8, castTime: 0.8, radius: 6, duration: 4, chill: CHILL_STORM, chillSeconds: CHILL_STORM_SECONDS, notes: 'Damages and slows everything in an area.' }),

  spell({ id: 'wall_of_ice', name: 'Wall of Ice', school: 'Ice Law', cls: 'magician', slot: 'utility', kind: 'wall', element: 'cold', ppCost: COST_UTILITY, fatigueCost: 30, cooldown: 12, castTime: 0.4, duration: 10, notes: 'Solid. Blocks movement and shots both ways.' }),
  spell({ id: 'wall_of_cold', name: 'Wall of Cold', school: 'Ice Law', cls: 'magician', slot: 'utility', kind: 'wall', element: 'cold', dice: RAMP_CONTROL, ppCost: COST_UTILITY, fatigueCost: 28, cooldown: 10, castTime: 0.4, duration: 8, notes: 'Passable, but damages anything that walks through.' }),

  spell({ id: 'mag_resist', name: 'Resist', nameTemplate: 'Resist {element}', elementChoices: PROTECTABLE, school: 'Protections', cls: 'magician', slot: 'defense', kind: 'buff', element: 'physical', ppCost: COST_DEFENSE, fatigueCost: 20, cooldown: 14, duration: 20, notes: 'Tab cycles the school it guards against while it is readied.' }),
];

// ─────────────────────────────────────────────────────────────────────────────
//  CLERIC — Heals, raises, and unresistable wounding. Innate: Call Dead.
// ─────────────────────────────────────────────────────────────────────────────
const CLERIC: SpellDef[] = [
  spell({ id: 'wounding', name: 'Wounding', school: 'Wounding', cls: 'cleric', slot: 'primary', kind: 'projectile', element: 'physical', dice: RAMP_PRIMARY, ppCost: COST_PRIMARY, fatigueCost: 16, cooldown: 0.4, projectileSpeed: 42, notes: 'A slow moving but unresistable physical attack deadly at close range.' }),

  spell({ id: 'bleeding', name: 'Bleeding', school: 'Wounding', cls: 'cleric', slot: 'secondary', kind: 'debuff', element: 'physical', dice: RAMP_BLEED, ticks: true, durations: BLEED_DURATIONS, ppCost: COST_SECONDARY, fatigueCost: 22, cooldown: 4, projectileSpeed: 38, notes: 'A damage over time projectile that also blocks enemy hit-point regeneration.' }),
  spell({ id: 'fracture', name: 'Fracture', school: 'Wounding', cls: 'cleric', slot: 'area', kind: 'debuff', element: 'physical', ppCost: COST_SECONDARY, fatigueCost: 24, cooldown: 7, projectileSpeed: 36, duration: 9, notes: 'Curses a target to receive increased damage from all sources.' }),
  spell({ id: 'wounding_triad', name: 'Wounding Triad', school: 'Wounding', cls: 'cleric', slot: 'secondary', kind: 'projectile', element: 'physical', dice: RAMP_TRIAD, volley: 3, ppCost: COST_SECONDARY, fatigueCost: 30, cooldown: 3, castTime: 0.3, projectileSpeed: 42, notes: 'Three axes at once, in a triangle. Each rolls and hits on its own, so a glancing throw still lands one.' }),

  spell({ id: 'healing', name: 'Healing', school: 'Healing', cls: 'cleric', slot: 'defense', kind: 'heal', element: 'physical', dice: RAMP_HEAL, ppCost: COST_CONTROL, fatigueCost: 20, cooldown: 6, notes: 'Self only.' }),

  spell({ id: 'heal_other', name: 'Heal Other', school: 'Healing', cls: 'cleric', slot: 'control', kind: 'heal-other', element: 'physical', dice: RAMP_HEAL, ppCost: COST_CONTROL, fatigueCost: 24, cooldown: 4, castTime: 0.3, notes: 'Targeted at a teammate in line of sight. Tier V is Full Heal.' }),
  spell({ id: 'cramp', name: 'Cramp', school: 'Wounding', cls: 'cleric', slot: 'area', kind: 'debuff', element: 'physical', ppCost: COST_CONTROL, fatigueCost: 18, cooldown: 6, projectileSpeed: 34, duration: 3, notes: 'Slowed rather than rooted. Cheaper and far more often available than Paralysis.' }),
  spell({ id: 'paralysis', name: 'Paralysis', school: 'Wounding', cls: 'cleric', slot: 'area', kind: 'debuff', element: 'physical', ppCost: COST_CONTROL, fatigueCost: 34, cooldown: 14, castTime: 0.5, projectileSpeed: 30, duration: 1.6, notes: 'Roots a single enemy in place for a short time. They cannot jump out of it either.' }),

  spell({ id: 'airwall', name: 'Airwall', tierNames: ['Airwall', 'Waterwall', 'Woodwall', 'Earthwall', 'Stonewall'], school: 'Barrier Law', cls: 'cleric', slot: 'utility', kind: 'wall', element: 'physical', ppCost: COST_UTILITY, fatigueCost: 26, cooldown: 12, castTime: 0.4, duration: 8, notes: 'Stops spells and slows bodies, improving to a stronger element at higher tiers.' }),
  spell({ id: 'dispell', name: 'Dispell', school: 'Barrier Law', cls: 'cleric', slot: 'utility', kind: 'debuff', element: 'physical', ppCost: COST_UTILITY, fatigueCost: 22, cooldown: 9, projectileSpeed: 44, notes: 'Strips resists, armor and walls. The counter to a stacked defensive enemy.' }),

  spell({ id: 'prayer', name: 'Prayer', school: 'Prayer', cls: 'cleric', slot: 'defense', kind: 'buff', element: 'physical', ppCost: COST_DEFENSE, fatigueCost: 20, cooldown: 16, duration: 18, notes: 'Flat reduction against every element at once.' }),
];

// ─────────────────────────────────────────────────────────────────────────────
//  MENTALIST — Light damage, mind attacks. Innate: flight and leaping.
// ─────────────────────────────────────────────────────────────────────────────
const MENTALIST: SpellDef[] = [
  spell({ id: 'shock_dart', name: 'Shock Dart', school: 'Brilliance', cls: 'mentalist', slot: 'primary', kind: 'projectile', element: 'light', dice: RAMP_PRIMARY, ppCost: COST_PRIMARY, fatigueCost: 15, cooldown: 0.3, projectileSpeed: 62, notes: 'Fastest primary in the game. Rewards flick aim from the air.' }),

  /*
   * A primary that cannot miss, which is the Mentalist's whole argument.
   *
   * It was a utility on the arc ramp, sitting behind Shock — which did the same
   * thing further and harder and made Jolts pointless. Shock is gone and Jolts
   * takes the primary ramp, so the choice at rank one is now between a dart that
   * rewards aim and an arc that does not, rather than between an arc and a
   * better arc.
   */
  spell({ id: 'jolts', name: 'Jolts', school: 'Mind Attack', cls: 'mentalist', slot: 'primary', kind: 'arc', element: 'light', dice: RAMP_PRIMARY_ARC, ppCost: COST_PRIMARY, fatigueCost: 12, cooldown: 0.6, range: 16, notes: 'Hits automatically — no aiming, and no dodging it. Shorter reach than a dart.' }),

  spell({ id: 'shock_bolt', name: 'Shock Bolt', school: 'Brilliance', cls: 'mentalist', slot: 'secondary', kind: 'bolt', element: 'light', dice: RAMP_SECONDARY, ppCost: COST_SECONDARY, fatigueCost: 24, cooldown: 1.5, castTime: 0.2, range: 50, notes: 'Heavier version of Shock Dart.' }),
  spell({ id: 'lightning_bolt', name: 'Lightning Bolt', school: 'Brilliance', cls: 'mentalist', slot: 'secondary', kind: 'bolt', element: 'light', dice: RAMP_SECONDARY, ppCost: COST_SECONDARY, fatigueCost: 28, cooldown: 2.0, castTime: 0.35, range: 55, farBonus: 1, notes: 'Hits harder the further it travels — one more damage for every ten metres, up to five at full reach. Punishes anyone crossing an open sightline.' }),

  spell({ id: 'electrocute', name: 'Electrocute', school: 'Mind Attack', cls: 'mentalist', slot: 'area', kind: 'ball', element: 'light', dice: RAMP_AREA, ppCost: COST_AREA, fatigueCost: 38, cooldown: 6, castTime: 0.5, projectileSpeed: 50, radius: 4, chainTargets: CHAIN_ELECTROCUTE, notes: 'Jumps from whoever it hits to one more nearby, and to two more from tier II.' }),

  spell({ id: 'pain', name: 'Pain', school: 'Mind Attack', cls: 'mentalist', slot: 'control', kind: 'touch', element: 'light', dice: RAMP_CONTROL, ppCost: COST_CONTROL, fatigueCost: 22, cooldown: 3, range: 14, shockDamage: PAIN_TICKS, notes: 'Lands the instant you fire, on whoever is under the crosshair, and goes on hurting for four seconds. Casting it again starts the four seconds over.' }),
  spell({ id: 'soul_shock', name: 'Soul Shock', school: 'Mind Attack', cls: 'mentalist', slot: 'control', kind: 'touch', element: 'light', dice: RAMP_CONTROL, ppCost: COST_CONTROL, fatigueCost: 36, cooldown: 10, castTime: 0.4, range: 14, notes: 'Drains power as well as hit points. Scales off the target’s missing PP.' }),

  /*
   * The Mentalist's protection, spent on somebody else.
   *
   * Every other guard in the game is cast on yourself; this is the only one that
   * is not, which is what makes it a *utility* rather than a defence — it is
   * something you do for the team, and the tab that picks the school makes it a
   * read of what the enemy has been throwing rather than a habit.
   */
  spell({ id: 'target_resist', name: 'Target Resist', nameTemplate: 'Target Resist {element}', elementChoices: PROTECTABLE, school: 'Damage Resist', cls: 'mentalist', slot: 'utility', kind: 'buff-other', element: 'physical', ppCost: COST_UTILITY, fatigueCost: 20, cooldown: 10, duration: 18, range: 26, notes: 'Guards a teammate against one school. Tab cycles which while it is readied.' }),
  spell({ id: 'blur', name: 'Blur', school: 'Movements', cls: 'mentalist', slot: 'utility', kind: 'buff', element: 'light', ppCost: COST_UTILITY, fatigueCost: 24, cooldown: 16, duration: 5, notes: 'Near-invisible while it lasts, and it breaks on casting anything offensive.' }),

  spell({ id: 'ment_resist', name: 'Resist', nameTemplate: 'Resist {element}', elementChoices: PROTECTABLE, school: 'Damage Resist', cls: 'mentalist', slot: 'defense', kind: 'buff', element: 'physical', ppCost: COST_DEFENSE, fatigueCost: 18, cooldown: 14, duration: 20, notes: 'Tab cycles the school it guards against while it is readied.' }),
  spell({ id: 'ment_armor', name: 'Elemental Armor', nameTemplate: '{element} Armor', elementChoices: PROTECTABLE, school: 'Damage Resist', cls: 'mentalist', slot: 'defense', kind: 'buff', element: 'physical', ppCost: COST_DEFENSE, fatigueCost: 26, cooldown: 20, duration: 30, notes: 'Half again the reduction of Resist and it lasts longer, longer cooldown. Tab cycles the school.' }),
];

// ─────────────────────────────────────────────────────────────────────────────
//  ARCANIST — Unresistable void and mana, plus floor traps. Cannot bias pools.
// ─────────────────────────────────────────────────────────────────────────────
const ARCANIST: SpellDef[] = [
  spell({ id: 'void_spike', name: 'Void Spike', school: 'Void Law', cls: 'arcanist', slot: 'primary', kind: 'projectile', element: 'void', dice: RAMP_PRIMARY, ppDrain: DRAIN_SPIKE, ppCost: COST_PRIMARY, fatigueCost: 18, cooldown: 0.4, projectileSpeed: 48, notes: 'Unresistable, and it tears a little power out of the target on hit.' }),
  spell({ id: 'manastrike', name: 'Manastrike', school: 'Mana Law', cls: 'arcanist', slot: 'primary', kind: 'projectile', element: 'mana', dice: RAMP_PRIMARY, ppCost: COST_PRIMARY, fatigueCost: 16, cooldown: 0.35, projectileSpeed: 56, notes: 'Unresistable. Drains nothing, but fires faster and flies faster than Void Spike.' }),

  spell({ id: 'void_bolt', name: 'Void Bolt', school: 'Void Law', cls: 'arcanist', slot: 'secondary', kind: 'bolt', element: 'void', dice: RAMP_SECONDARY, ppDrain: DRAIN_BOLT, ppCost: COST_SECONDARY, fatigueCost: 26, cooldown: 1.7, castTime: 0.25, range: 44, notes: 'Unresistable, and it drains the target’s power on hit.' }),
  spell({ id: 'mana_bolt', name: 'Mana Bolt', school: 'Mana Law', cls: 'arcanist', slot: 'secondary', kind: 'bolt', element: 'mana', dice: RAMP_SECONDARY, ppCost: COST_SECONDARY, fatigueCost: 26, cooldown: 1.4, castTime: 0.25, range: 42, notes: 'Unresistable. No drain, but with a lower cooldown.' }),

  spell({ id: 'void_ball', name: 'Void Ball', tierNames: ['Void Ball', 'Void Ball', 'Void Ball', 'Void Ball', 'Utter Void'], school: 'Void Law', cls: 'arcanist', slot: 'area', kind: 'ball', element: 'void', dice: RAMP_AREA, ppDrain: DRAIN_BALL, ppCost: COST_AREA, fatigueCost: 42, cooldown: 6, castTime: 0.55, projectileSpeed: 24, radius: 5, notes: 'Drains power from everything caught in the blast.' }),
  spell({ id: 'mana_ball', name: 'Mana Ball', tierNames: ['Mana Ball', 'Mana Ball', 'Mana Ball', 'Mana Ball', 'Mana Storm'], school: 'Mana Law', cls: 'arcanist', slot: 'area', kind: 'ball', element: 'mana', dice: RAMP_AREA, ppCost: COST_AREA, fatigueCost: 42, cooldown: 5, castTime: 0.55, projectileSpeed: 24, radius: 5, notes: 'No drain, but a second off the wait compared with Void Ball.' }),

  spell({ id: 'sign_explode', name: 'Sign of Explosion', school: 'Arcane Signs', cls: 'arcanist', slot: 'control', kind: 'sign', element: 'void', dice: RAMP_AREA, ppCost: COST_CONTROL, fatigueCost: 30, cooldown: 8, duration: 45, radius: 4, notes: 'Laid on the floor. Triggers on enemy contact. Visible if you are looking for it.' }),
  spell({ id: 'sign_unpower', name: 'Sign of Unpower', school: 'Arcane Signs', cls: 'arcanist', slot: 'control', kind: 'sign', element: 'mana', ppCost: COST_CONTROL, fatigueCost: 26, cooldown: 8, duration: 45, radius: 3, notes: 'Empties the power of whoever steps on it. Lethal around a chokepoint.' }),
  spell({ id: 'sign_hesitate', name: 'Sign of Hesitation', school: 'Arcane Signs', cls: 'arcanist', slot: 'control', kind: 'sign', element: 'void', ppCost: COST_CONTROL, fatigueCost: 24, cooldown: 8, duration: 45, radius: 3, notes: 'Roots briefly. Best laid on stairs and blind corners.' }),

  spell({ id: 'sign_expulse', name: 'Sign of Expulsion', school: 'Arcane Signs', cls: 'arcanist', slot: 'utility', kind: 'sign', element: 'void', ppCost: COST_UTILITY, fatigueCost: 22, cooldown: 10, duration: 45, radius: 3, notes: 'Launches anyone who touches it into the air, including you.' }),
  spell({ id: 'mana_wall', name: 'Mana Wall', school: 'Mana Law', cls: 'arcanist', slot: 'utility', kind: 'wall', element: 'mana', ppCost: COST_UTILITY, fatigueCost: 28, cooldown: 12, castTime: 0.4, duration: 9, notes: 'Drains the power of anyone who pushes through it.' }),

  spell({ id: 'arc_resist', name: 'Resist', nameTemplate: 'Resist {element}', elementChoices: PROTECTABLE, school: 'Arcane Protection', cls: 'arcanist', slot: 'defense', kind: 'buff', element: 'physical', ppCost: COST_DEFENSE, fatigueCost: 20, cooldown: 14, duration: 20, notes: 'Tab cycles the school it guards against while it is readied.' }),
];

/**
 * The two halves of what Call Dead used to be, and Flight.
 *
 * They are spells for every purpose that matters — they cost, they cool down,
 * they tier, and they appear on the bar — but they are not carried in a loadout
 * slot. A Cleric has them because they are a Cleric, so the grimoire never
 * offers them and no loadout has to spend a slot on the thing that defines the
 * class.
 */
export const INNATE_SPELLS: readonly SpellDef[] = [
  spell({
    id: 'call_ghost', name: 'Call Ghost', school: 'Call Dead', cls: 'cleric',
    slot: 'innate', kind: 'call', element: 'light',
    // A steep price in breath and almost none in power: pulling somebody back
    // across a room is exhausting rather than expensive, and a Cleric who does
    // it twice in a row should be in no state to run afterwards.
    ppCost: [2, 2, 2, 2, 2], fatigueCost: 50, cooldown: 6,
    unlockLevels: [2, 2, 2, 2, 2],
    notes: 'Pulls the nearest fallen ally on your side to your feet. They arrive '
      + 'still a ghost; raising them is a second act.',
  }),
  spell({
    id: 'resurrect', name: 'Resurrect', school: 'Call Dead', cls: 'cleric',
    slot: 'innate', kind: 'resurrect', element: 'light',
    // A full bar at level one, and it stays a real commitment: each tier costs
    // another six and gives back another fifteen per cent of them.
    ppCost: [6, 12, 18, 24, 30], fatigueCost: 10, cooldown: 8,
    unlockLevels: [1, 7, 14, 21, 28],
    notes: 'Brings a ghost at your feet back to life with a share of their '
      + 'health, power and breath. The share grows with the tier.',
  }),
  spell({
    id: 'flight', name: 'Flight', school: 'Mind', cls: 'mentalist',
    slot: 'innate', kind: 'flight', element: 'mana',
    // No cost of its own: flight is paid for continuously while it is held, in
    // fatigue and in power, which no cast-time price could describe. The tile
    // exists to say whether it is available, not to be pressed.
    ppCost: [0, 0, 0, 0, 0], fatigueCost: 0, cooldown: 0,
    unlockLevels: [1, 1, 1, 1, 1],
    notes: 'Held, not cast. Drains fatigue and power together, and ends when '
      + 'either runs out.',
  }),
];

/** The innates this class carries, in the order they appear on the bar. */
export function innatesFor(cls: ClassId): readonly SpellDef[] {
  return INNATE_SPELLS.filter((s) => s.cls === cls);
}

export const ALL_SPELLS: readonly SpellDef[] = [...MAGICIAN, ...CLERIC, ...MENTALIST, ...ARCANIST];

/**
 * The global cooldown: every cast locks every slot for this long.
 *
 * Derived from the catalogue rather than written down, so retuning the fastest
 * spell cannot silently leave the two out of step. Without it a player cycles
 * 1-2-3 and fires three spells in the time one was meant to take, which makes
 * every individual cooldown in the game advisory.
 */
export const GLOBAL_COOLDOWN = ALL_SPELLS.reduce(
  (fastest, spell) => Math.min(fastest, spell.cooldown), Infinity,
);

/**
 * Every spell by id, innates included.
 *
 * `ALL_SPELLS` is the *choosable* catalogue and the grimoire reads it, so the
 * innates are deliberately not in it — but everything that looks a spell up by
 * id needs to find them, or a Cleric's own two spells do not exist.
 */
export const SPELLS_BY_ID: Record<string, SpellDef> = Object.fromEntries(
  [...ALL_SPELLS, ...INNATE_SPELLS].map((s) => [s.id, s]),
);

/**
 * What the HUD should call this spell right now: the tier's own name if it has
 * a ladder, or the tuned element if it has one.
 */
export function displayName(spell: SpellDef, tier: number, elementIndex = 0): string {
  const choices = spell.elementChoices;
  if (choices && choices.length) {
    const element = choices[((elementIndex % choices.length) + choices.length) % choices.length];
    return (spell.nameTemplate ?? spell.name).replace('{element}', ELEMENT_LABEL[element]);
  }
  const names = spell.tierNames;
  if (!names) return spell.name;
  return names[Math.min(names.length - 1, Math.max(0, tier - 1))];
}

/** The element a tunable spell is currently set to, or its own fixed one. */
export function chosenElement(spell: SpellDef, elementIndex: number): Element {
  const choices = spell.elementChoices;
  if (!choices || !choices.length) return spell.element;
  return choices[((elementIndex % choices.length) + choices.length) % choices.length];
}

/**
 * Name plus tier numeral, as the spell bar shows it — "Sharp Edges I".
 * Every spell ramps, so every spell carries its numeral.
 */
export function slotLabel(spell: SpellDef, tier: number, elementIndex = 0): string {
  const numeral = ROMAN[Math.min(ROMAN.length - 1, Math.max(0, tier - 1))];
  return `${displayName(spell, tier, elementIndex)} ${numeral}`;
}

export function spellsFor(cls: ClassId, slot?: SlotRole): SpellDef[] {
  return ALL_SPELLS.filter((s) => s.cls === cls && (slot === undefined || s.slot === slot));
}

/** The first candidate in each slot, used as the starting loadout. */
export function defaultLoadout(cls: ClassId): string[] {
  return SLOT_ORDER.map((slot) => spellsFor(cls, slot)[0]?.id).filter(Boolean) as string[];
}

/** Innate abilities that cost no loadout slot. */
export const INNATE: Partial<Record<ClassId, string[]>> = {
  cleric: ['Call Dead — draw a fallen teammate’s ghost to you and raise it'],
  mentalist: ['Flight and Leaping — sustained by fatigue rather than power'],
};
